import React from 'react';
import { Dialog } from 'primereact/dialog';
import type { FilaPropuestaReparto, ModoReparto, RepartoPropuesta } from '../types/metas.types';
import { TEXTO_META_A_BODEGUERO } from '../utils/bodeguero';
import { TEXTO_META_A_CASA } from '../utils/casa';
import { pesos, pesosConSigno } from '../utils/formato';
import { cuadraConLaTienda, metasQueNoCambian } from '../utils/reparto';
import { CLASE_BOTON, CLASE_BOTON_PRIMARIO, CLASE_OVERLAY, cx } from './ui/clases';

interface Props {
  /** La propuesta de la API; `null` = cerrado. */
  propuesta: RepartoPropuesta | null;
  /** «Aplicar»: la pantalla pone las metas propuestas y el autoguardado las guarda. */
  onAplicar: () => void;
  /** «Cancelar», la ✕ o Escape: no cambia nada. */
  onHide: () => void;
  /** Teléfono: el diálogo a pantalla completa y una tarjeta por persona. */
  movil?: boolean;
  /**
   * `iguales` (ronda F, 23-oct-2026): «Repartir en partes iguales», con otro
   * encabezado y otro texto. Por omisión, «según venta promedio», como antes.
   */
  modo?: ModoReparto;
  /** «Cambiar selección» (sólo en partes iguales): vuelve al paso 1 con lo marcado. */
  onCambiarSeleccion?: () => void;
}

/** «+$8,000» con centavos sólo si los hay (el residuo puede traerlos). */
const mas = (n: number): string => (Math.round(n * 100) === 0 ? '$0' : `+${pesos(n)}`);

/** «Sin meta» y «Sin promedio» en partes iguales: ahí también recibe quien no los tiene (ronda F). */
const metaActualDe = (f: FilaPropuestaReparto): string =>
  f.metaActual === null || f.metaActual === undefined ? 'Sin meta' : pesos(f.metaActual);
const promedioDe = (f: FilaPropuestaReparto): string =>
  f.ventaPromedio === null || f.ventaPromedio === undefined ? 'Sin promedio' : `Promedio ${pesos(f.ventaPromedio)}`;

/**
 * VISTA PREVIA de «Repartir según venta promedio» (SPEC §6, fase 6): quién
 * recibe cuánto, el total que cuadra con la meta de tienda y quién quedó fuera
 * y por qué. Nada se guarda aquí: «Aplicar» pone las metas en la captura y el
 * autoguardado las manda como cualquier cambio (se pueden corregir después).
 */
export const RepartirDialog: React.FC<Props> = ({ propuesta, onAplicar, onHide, movil = false, modo = 'promedio', onCambiarSeleccion }) => {
  const p = propuesta;
  const otras = p ? metasQueNoCambian(p) : 0;
  const cuadra = p ? cuadraConLaTienda(p) : false;
  const n = p?.filas.length ?? 0;
  const iguales = modo === 'iguales';
  // «Se le está colocando meta al bodeguero / a la Casa» (ronda F): lo marca la propuesta.
  const bodegueros = (p?.filas ?? []).filter((f) => f.aviso === 'bodeguero');
  const casa = (p?.filas ?? []).find((f) => f.aviso === 'casa');

  const pie = (
    <div className={cx('flex gap-2', movil ? 'flex-col-reverse' : 'justify-end')}>
      <button type="button" className={cx(CLASE_BOTON, movil && 'w-full')} onClick={onHide}>
        Cancelar
      </button>
      {iguales && onCambiarSeleccion && (
        <button type="button" className={cx(CLASE_BOTON, movil && 'w-full')} onClick={onCambiarSeleccion}>
          Cambiar selección
        </button>
      )}
      <button type="button" className={cx(CLASE_BOTON_PRIMARIO, movil && 'w-full')} onClick={onAplicar} disabled={!p || !n}>
        Aplicar
      </button>
    </div>
  );

  const celdaNum = 'num whitespace-nowrap px-3 py-2.5 text-right';
  const th = 'px-3 py-2 text-metas-th text-metas-texto-2';

  return (
    <Dialog
      // La ✕ en español: el locale global de PrimeReact sigue en `en` («Close»).
      ariaCloseIconLabel="Cerrar"
      className={CLASE_OVERLAY}
      header={iguales ? 'Repartir en partes iguales' : 'Repartir según venta promedio'}
      visible={!!p}
      onHide={onHide}
      footer={pie}
      // Como la vista previa del Excel: en teléfono, a pantalla completa y
      // controlado (`onMaximize`), para que al girarlo no se quede a medias.
      maximized={movil}
      onMaximize={() => undefined}
      style={movil ? { width: '100vw', maxWidth: '100vw', margin: 0 } : { width: '95vw', maxWidth: 760 }}
      modal
    >
      {p && (
        <div className="flex flex-col gap-4 text-sm text-metas-texto" data-testid="repartir-vista-previa">
          {iguales ? (
            <p className="m-0 text-metas-texto-2">
              Faltan <b className="num font-semibold text-metas-texto">{pesos(p.porAsignar)}</b> para la meta de tienda (
              <span className="num">{pesos(p.metaTienda)}</span>). Se reparten en partes iguales entre{' '}
              {n === 1 ? 'la persona que elegiste' : `las ${n} personas que elegiste`}; cada parte va en múltiplos de{' '}
              {pesos(p.multiplo)} y la diferencia del redondeo la absorbe quien más vende. Revisa la propuesta: al aplicarla se guarda sola y la
              puedes corregir.
            </p>
          ) : (
            <p className="m-0 text-metas-texto-2">
              Faltan <b className="num font-semibold text-metas-texto">{pesos(p.porAsignar)}</b> para la meta de tienda (
              <span className="num">{pesos(p.metaTienda)}</span>). Se reparten entre {n === 1 ? 'la persona' : `las ${n} personas`} que ya
              tienen meta, en proporción a su venta promedio; cada parte va en múltiplos de {pesos(p.multiplo)} y la diferencia del
              redondeo la absorbe quien más vende. Revisa la propuesta: al aplicarla se guarda sola y la puedes corregir.
            </p>
          )}

          {movil ? (
            <ul className="m-0 flex list-none flex-col gap-2 p-0" aria-label="Metas propuestas">
              {p.filas.map((f) => (
                <li key={f.key} className="rounded-metas-control border border-metas-borde-suave px-3 py-2.5" data-testid="repartir-fila">
                  <div className="font-semibold [overflow-wrap:anywhere]">{f.nombre}</div>
                  <div className="num text-xs text-metas-texto-2">{promedioDe(f)}</div>
                  <div className="mt-1.5 grid grid-cols-3 gap-2 text-xs">
                    <div className="flex flex-col">
                      <span className="text-metas-texto-2">Actual</span>
                      <span className="num text-sm">{metaActualDe(f)}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-metas-texto-2">Propuesta</span>
                      <span className="num text-sm font-semibold">{pesos(f.metaPropuesta)}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-metas-texto-2">Diferencia</span>
                      <span className="num text-sm text-metas-exito">{mas(f.diferencia)}</span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <table className="w-full border-collapse" aria-label="Metas propuestas">
              <thead>
                <tr className="border-b border-metas-borde-suave bg-metas-cabecera text-left">
                  <th scope="col" className={th}>
                    Colaborador
                  </th>
                  <th scope="col" className={cx(th, 'text-right')}>
                    Meta actual
                  </th>
                  <th scope="col" className={cx(th, 'text-right')}>
                    Propuesta
                  </th>
                  <th scope="col" className={cx(th, 'text-right')}>
                    Diferencia
                  </th>
                </tr>
              </thead>
              <tbody>
                {p.filas.map((f) => (
                  <tr key={f.key} className="border-b border-metas-borde-suave" data-testid="repartir-fila">
                    <td className="px-3 py-2.5">
                      <div className="font-semibold">{f.nombre}</div>
                      <div className="num text-xs text-metas-texto-2">{promedioDe(f)}</div>
                    </td>
                    <td className={celdaNum}>{metaActualDe(f)}</td>
                    <td className={cx(celdaNum, 'font-semibold')}>{pesos(f.metaPropuesta)}</td>
                    <td className={cx(celdaNum, 'text-metas-exito')}>{mas(f.diferencia)}</td>
                  </tr>
                ))}
                {Math.round(otras * 100) !== 0 && (
                  <tr className="border-b border-metas-borde-suave text-metas-texto-2">
                    <td className="px-3 py-2.5">Metas que no cambian</td>
                    <td className={celdaNum}>{pesos(otras)}</td>
                    <td className={celdaNum}>{pesos(otras)}</td>
                    <td className={celdaNum}>$0</td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr className="font-semibold" data-testid="repartir-total">
                  <th scope="row" className="px-3 py-2.5 text-left">
                    Total de la tienda
                  </th>
                  <td className={celdaNum}>{pesos(p.sumaActual)}</td>
                  <td className={celdaNum}>{pesos(p.sumaPropuesta)}</td>
                  <td className={cx(celdaNum, 'text-metas-exito')}>{mas(p.porAsignar)}</td>
                </tr>
              </tfoot>
            </table>
          )}

          {movil && (
            <div className="flex flex-col gap-1 rounded-metas-control bg-metas-neutro-fondo px-3 py-2.5" data-testid="repartir-total">
              {Math.round(otras * 100) !== 0 && (
                <div className="flex justify-between gap-2 text-xs text-metas-texto-2">
                  <span>Metas que no cambian</span>
                  <span className="num">{pesos(otras)}</span>
                </div>
              )}
              <div className="flex justify-between gap-2 font-semibold">
                <span>Total de la tienda</span>
                <span className="num">{pesos(p.sumaPropuesta)}</span>
              </div>
              <div className="flex justify-between gap-2 text-xs text-metas-texto-2">
                <span>Antes</span>
                <span className="num">
                  {pesos(p.sumaActual)} ({pesosConSigno(-p.porAsignar)})
                </span>
              </div>
            </div>
          )}

          <div
            role="status"
            className={cx(
              'flex items-center gap-2 rounded-metas-control px-3 py-2 text-[13px] font-semibold',
              cuadra ? 'bg-metas-exito-fondo text-metas-exito' : 'bg-metas-aviso-fondo text-metas-aviso',
            )}
            data-testid="repartir-cuadre"
          >
            <i className={cx('pi text-xs', cuadra ? 'pi-check' : 'pi-exclamation-triangle')} aria-hidden="true" />
            {cuadra
              ? `Cuadra con la meta de tienda: ${pesos(p.metaTienda)}`
              : `La suma propuesta (${pesos(p.sumaPropuesta)}) no es la meta de tienda (${pesos(p.metaTienda)})`}
          </div>

          {(bodegueros.length > 0 || !!casa) && (
            <div className="flex flex-col gap-1" data-testid="repartir-avisos">
              {bodegueros.map((b) => (
                <p
                  key={b.key}
                  className="m-0 flex items-start gap-2 rounded-metas-control bg-metas-aviso-fondo px-3 py-2 text-[13px] font-semibold text-metas-aviso"
                >
                  <i className="pi pi-exclamation-triangle mt-0.5 text-xs" aria-hidden="true" />
                  <span className="[overflow-wrap:anywhere]">
                    {TEXTO_META_A_BODEGUERO}: {b.nombre}
                  </span>
                </p>
              ))}
              {casa && (
                <p className="m-0 flex items-start gap-2 rounded-metas-control bg-metas-aviso-fondo px-3 py-2 text-[13px] font-semibold text-metas-aviso">
                  <i className="pi pi-exclamation-triangle mt-0.5 text-xs" aria-hidden="true" />
                  <span className="[overflow-wrap:anywhere]">
                    {TEXTO_META_A_CASA}: {casa.nombre}
                  </span>
                </p>
              )}
            </div>
          )}

          {p.omitidas.length > 0 && (
            <div className="flex flex-col gap-1.5" data-testid="repartir-omitidas">
              <span className="text-metas-etiqueta uppercase text-metas-texto-2">
                No reciben ({p.omitidas.length})
              </span>
              <ul className="m-0 flex list-none flex-col gap-1 p-0 text-[13px]">
                {p.omitidas.map((o) => (
                  <li key={o.key}>
                    <b className="font-semibold">{o.nombre}</b>: <span className="text-metas-texto-2">{o.razon}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
};

export default RepartirDialog;
